"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, Pencil, Plus, Trash2 } from "lucide-react";

import { Button, EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, ThemedSelect } from "@/components/ui-system/select";
import { isAppHttpError } from "@/lib/api/client";
import { createPlatformUser, deletePlatformUser, listPlatformUsers, updatePlatformUser } from "@/lib/api/endpoints/platform-users";
import { listPlatformRoles } from "@/lib/api/endpoints/platform-roles";
import type { PlatformUserCreateBody, PlatformUserDto } from "@/lib/api/types/enterprise";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

type DialogMode = "create" | "edit" | null;

type PlatformUserForm = {
  email: string;
  name: string;
  phone: string;
  onboarding_mode: "invitation" | "manual";
  password: string;
  confirm_password: string;
  password_acknowledged: boolean;
  platform_role_id: string;
  is_active: boolean;
};

const EMPTY_FORM: PlatformUserForm = {
  email: "",
  name: "",
  phone: "",
  onboarding_mode: "invitation",
  password: "",
  confirm_password: "",
  password_acknowledged: false,
  platform_role_id: "",
  is_active: true,
};

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) {
    const detail = (() => {
      const raw = err.details;
      if (!raw || typeof raw !== "object" || !("detail" in raw)) {
        return null;
      }
      const entries = (raw as { detail?: unknown }).detail;
      return Array.isArray(entries) ? entries : null;
    })();

    if (detail?.length) {
      const first = detail[0] as { loc?: unknown; msg?: unknown; ctx?: unknown } | undefined;
      const loc = Array.isArray(first?.loc) ? first?.loc.map((item) => String(item).toLowerCase()) : [];
      const msg = typeof first?.msg === "string" ? first.msg.toLowerCase() : "";
      const ctxReason =
        first?.ctx && typeof first.ctx === "object" && "reason" in first.ctx && typeof (first.ctx as { reason?: unknown }).reason === "string"
          ? ((first.ctx as { reason: string }).reason.toLowerCase())
          : "";

      if (loc.includes("email") || msg.includes("email") || ctxReason.includes("email")) {
        return "Enter a valid email address.";
      }
      if (loc.includes("phone") || msg.includes("phone") || ctxReason.includes("phone")) {
        return "Enter a valid 10-digit phone number.";
      }
      if (loc.includes("confirm_password") || msg.includes("confirmation does not match")) {
        return "Password and confirm password must match.";
      }
      if (loc.includes("password_acknowledged") || msg.includes("noted down the password") || ctxReason.includes("noted down the password")) {
        return "Confirm that you have noted down the password.";
      }
      if ((loc.includes("password") || loc.includes("confirm_password")) && (msg.includes("required") || ctxReason.includes("required"))) {
        return "Enter and confirm the password for manual onboarding.";
      }
      return "Please check the highlighted form details and try again.";
    }

    const message = err.message || "";
    const normalized = message.toLowerCase();
    if (normalized.includes("valid email") || normalized.includes("email address must")) {
      return "Enter a valid email address.";
    }
    if (normalized.includes("phone")) {
      return "Enter a valid 10-digit phone number.";
    }
    if (normalized.includes("password confirmation does not match")) {
      return "Password and confirm password must match.";
    }
    if (normalized.includes("noted down the password")) {
      return "Confirm that you have noted down the password.";
    }
    if (normalized.includes("password and confirmation are required")) {
      return "Enter and confirm the password for manual onboarding.";
    }
    if (normalized.startsWith("[{") || normalized.includes("\"loc\"")) {
      return "Please check the form details and try again.";
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Request failed";
}

export function PlatformUsersPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const resetWorkspaceChrome = useWorkspaceChromeStore((s) => s.resetWorkspaceChrome);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [editing, setEditing] = useState<PlatformUserDto | null>(null);
  const [form, setForm] = useState<PlatformUserForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setWorkspaceChrome({ title: "Users", search: null });
    return () => resetWorkspaceChrome();
  }, [resetWorkspaceChrome, setWorkspaceChrome]);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const rolesQuery = useQuery({
    queryKey: ["platform-roles", "options"],
    queryFn: listPlatformRoles,
    staleTime: 60_000,
  });

  const usersQuery = useQuery({
    queryKey: ["platform-users", search],
    queryFn: () => listPlatformUsers({ search, limit: 200, offset: 0 }),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: PlatformUserCreateBody) => createPlatformUser(body),
    onSuccess: async () => {
      setDialog(null);
      setEditing(null);
      setForm(EMPTY_FORM);
      setFormError(null);
      setToast("Platform user created.");
      await qc.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { userId: string; body: Partial<PlatformUserCreateBody> }) =>
      updatePlatformUser(payload.userId, payload.body),
    onSuccess: async () => {
      setDialog(null);
      setEditing(null);
      setForm(EMPTY_FORM);
      setFormError(null);
      setToast("Platform user updated.");
      await qc.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePlatformUser,
    onSuccess: async () => {
      setToast("Platform user removed.");
      await qc.invalidateQueries({ queryKey: ["platform-users"] });
    },
    onError: (error) => setFormError(errorDetail(error)),
  });

  const roleOptions = useMemo(
    () => [
      { value: "", label: "No platform role" },
      ...(rolesQuery.data?.items ?? []).map((role) => ({ value: role.id, label: role.name })),
    ],
    [rolesQuery.data?.items],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialog("create");
  };

  const openEdit = (user: PlatformUserDto) => {
    setEditing(user);
    setForm({
      email: user.email,
      name: user.name ?? "",
      phone: user.phone ?? "",
      onboarding_mode: "invitation",
      password: "",
      confirm_password: "",
      password_acknowledged: false,
      platform_role_id: user.platform_role_id ?? "",
      is_active: user.is_active,
    });
    setFormError(null);
    setDialog("edit");
  };

  const handleSubmit = () => {
    setFormError(null);
    if (!form.email.trim() || !form.name.trim()) {
      setFormError("Email and name are required");
      return;
    }
    if (dialog === "create") {
      createMutation.mutate({
        email: form.email.trim(),
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        onboarding_mode: form.onboarding_mode,
        password: form.onboarding_mode === "manual" ? form.password : null,
        confirm_password: form.onboarding_mode === "manual" ? form.confirm_password : null,
        password_acknowledged: form.password_acknowledged,
        platform_role_id: form.platform_role_id || null,
        is_active: form.is_active,
      });
      return;
    }
    if (!editing) {
      return;
    }
    updateMutation.mutate({
      userId: editing.id,
      body: {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        platform_role_id: form.platform_role_id || null,
        is_active: form.is_active,
      },
    });
  };

  const rows = usersQuery.data?.items ?? [];

  return (
    <EnterprisePageShell hideHeader kicker="Platform" title="Users" description="Create and manage internal operators who work on the platform side.">
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
      <ListSurface>
        <ListSurfaceHeader
          title="User directory"
          description="Internal operators are separate from customer workspace accounts."
          meta={<ListCount total={usersQuery.data?.total ?? 0} filtered={rows.length} noun={rows.length === 1 ? "user" : "users"} />}
          controls={
            <ListToolbar>
              <ListToolbarGroup grow>
                <input
                  className={tableHeaderSearchInputClass}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search name, email, role..."
                />
              </ListToolbarGroup>
              {searchInput.trim() ? <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => { setSearchInput(""); setSearch(""); }} /> : null}
              <ToolbarIconButton icon={Plus} label="Add user" variant="primary" onClick={openCreate} />
            </ListToolbar>
          }
        />
        <ListSurfaceBody className="overflow-x-auto">
          {usersQuery.isLoading ? (
            <div className="p-ds-4" aria-label="Loading platform users">
              {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={5} />)}
            </div>
          ) : usersQuery.isError ? (
            <div className="px-4 py-6 text-sm text-error">{errorDetail(usersQuery.error)}</div>
          ) : rows.length === 0 ? (
            <EmptyState title="No internal users found" description="Create an internal user to delegate platform operations without giving full super admin access." className="rounded-none border-0 border-t" />
          ) : (
            <table className="min-w-full border-collapse">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Email</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Name</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Status</th>
                  <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user, index) => (
                  <tr key={user.id} className="border-t border-card-border" style={{ background: index % 2 === 1 ? "rgba(59,130,246,0.05)" : "var(--bg-surface)" }}>
                    <td className="px-4 py-4 align-middle text-sm text-foreground">{user.email}</td>
                    <td className="px-4 py-4 align-middle text-sm text-foreground">{user.name ?? "-"}</td>
                    <td className="px-4 py-4 align-middle text-sm text-muted">{user.role === "super_admin" ? "Super admin" : (user.platform_role_name ?? "No platform role")}</td>
                    <td className="px-4 py-4 align-middle text-sm text-muted">{user.is_active ? user.account_status : "Disabled"}</td>
                    <td className="px-4 py-4 align-middle">
                      <div className="flex justify-end gap-2">
                        {user.role !== "super_admin" || user.id === me?.id ? (
                          <button type="button" className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-primary/15 bg-primary/10 px-[10px] text-primary" onClick={() => openEdit(user)} title={`Edit ${user.email}`}>
                            <Pencil size={14} />
                          </button>
                        ) : null}
                        {user.role !== "super_admin" ? (
                          <button type="button" className="inline-flex min-h-9 min-w-[36px] items-center justify-center rounded-control border border-error/25 bg-error/10 px-[10px] text-error" onClick={() => deleteMutation.mutate(user.id)} title={`Delete ${user.email}`}>
                            <Trash2 size={14} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ListSurfaceBody>
      </ListSurface>

      {dialog ? (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-slate-950/25 p-4 backdrop-blur-sm sm:p-6">
          <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-card-border bg-surface-elevated shadow-overlay sm:max-h-[calc(100vh-3rem)]">
            <div className="mb-0 border-b border-card-border px-6 pb-4 pt-6">
              <h2 className="text-lg font-semibold text-foreground">{dialog === "create" ? "Add user" : "Edit user"}</h2>
              <p className="text-sm text-muted">Platform users are internal operators. Customer accounts stay in Customers.</p>
            </div>
            <div className="grid flex-1 gap-4 overflow-y-auto px-6 py-5">
              <label className="grid gap-2 text-sm">
                <span className="font-medium text-foreground">Email</span>
                <input className="ui-control rounded-control px-4 py-3" value={form.email} disabled={dialog === "edit"} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium text-foreground">Name</span>
                <input className="ui-control rounded-control px-4 py-3" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium text-foreground">Phone</span>
                <input className="ui-control rounded-control px-4 py-3" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Platform role</span>
                  <ThemedSelect value={form.platform_role_id} onChange={(value) => setForm((current) => ({ ...current, platform_role_id: value }))} options={roleOptions} ariaLabel="Platform role" triggerClassName={tableHeaderSelectTriggerClass} />
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Status</span>
                  <ThemedSelect value={form.is_active ? "active" : "disabled"} onChange={(value) => setForm((current) => ({ ...current, is_active: value === "active" }))} options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }]} ariaLabel="Account status" triggerClassName={tableHeaderSelectTriggerClass} />
                </label>
              </div>
              {dialog === "create" ? (
                <>
                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-foreground">Onboarding</span>
                    <ThemedSelect value={form.onboarding_mode} onChange={(value) => setForm((current) => ({ ...current, onboarding_mode: value as "invitation" | "manual" }))} options={[{ value: "invitation", label: "Email invitation" }, { value: "manual", label: "Manual" }]} ariaLabel="Onboarding mode" triggerClassName={tableHeaderSelectTriggerClass} />
                  </label>
                  {form.onboarding_mode === "manual" ? (
                    <>
                      <label className="grid gap-2 text-sm">
                        <span className="font-medium text-foreground">Password</span>
                        <input type="password" className="ui-control rounded-control px-4 py-3" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
                      </label>
                      <label className="grid gap-2 text-sm">
                        <span className="font-medium text-foreground">Confirm password</span>
                        <input type="password" className="ui-control rounded-control px-4 py-3" value={form.confirm_password} onChange={(event) => setForm((current) => ({ ...current, confirm_password: event.target.value }))} />
                      </label>
                      <label className="flex items-center gap-2 text-sm text-muted">
                        <input type="checkbox" checked={form.password_acknowledged} onChange={(event) => setForm((current) => ({ ...current, password_acknowledged: event.target.checked }))} />
                        I have noted down the password
                      </label>
                    </>
                  ) : null}
                </>
              ) : null}
              {formError ? <div className="rounded-control border border-error/30 bg-error/10 px-3 py-2 text-caption text-error" role="alert">{formError}</div> : null}
              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-card-border bg-surface-elevated pt-4">
                <button type="button" onClick={() => setDialog(null)} className="h-10 rounded-lg border border-card-border px-4 text-sm font-semibold text-foreground">Cancel</button>
                <Button type="button" variant="primary" size="md" disabled={createMutation.isPending || updateMutation.isPending} onClick={handleSubmit}>
                  {dialog === "create" ? (createMutation.isPending ? "Creating..." : "Create user") : (updateMutation.isPending ? "Saving..." : "Save user")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </EnterprisePageShell>
  );
}
